import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, BookOpen, Image as ImageIcon } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { Textarea } from '@/components/ui/Textarea'
import { RichTextEditor } from '@/components/ui/RichTextEditor'
import { Select } from '@/components/ui/Select'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import {
  ManualBlocksService,
  type ManualBlock,
  type ManualBlockPayload,
} from '@/services/modules/ManualBlocksService'
import { ProposalSetupService, type ProposalCoverageItem } from '@/services/modules/ProposalSetupService'

/** Converte lista ⇄ texto (um item por linha). */
const toLines = (arr?: string[]) => (arr ?? []).join('\n')
const fromLines = (txt: string) => txt.split('\n').map(s => s.trim()).filter(Boolean)

/**
 * Conteúdo do Manual de Utilização. Cada bloco descreve um benefício e é
 * incluído no manual gerado quando o serviço correspondente está incluso na
 * proposta. Blocos sem serviço são fixos.
 */
export function SettingsManualBlocksPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.manual_blocks.create')
  const canEdit = can('settings.manual_blocks.edit')
  const canDelete = can('settings.manual_blocks.delete')
  const canIcon = can('settings.manual_blocks.icon')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar bloco' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList<ManualBlock>(
    ManualBlocksService,
    { size: 25, reloadKey: reload, filters: sf.filters },
  )
  const [items, setItems] = useState<ProposalCoverageItem[]>([])
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: ManualBlock }>({ open: false })
  const [openDelete, setOpenDelete] = useState<ManualBlock | null>(null)

  useEffect(() => {
    ProposalSetupService.items().then(rows => setItems(rows.filter(i => i.status === 'ATIVO'))).catch(() => setItems([]))
  }, [])

  const exp = useDataTableExport({
    filename: 'manual-utilizacao',
    service: ManualBlocksService,
    filters: sf.filters,
    columns: [
      { header: 'Ordem',     accessor: (b: ManualBlock) => b.sortOrder ?? 0 },
      { header: 'Bloco',     accessor: (b: ManualBlock) => b.title },
      { header: 'Subtítulo', accessor: (b: ManualBlock) => b.subtitle ?? '' },
      { header: 'Cobertura', accessor: (b: ManualBlock) => b.coverageItemName || 'Fixo (sempre entra)' },
      { header: 'Status',    accessor: (b: ManualBlock) => b.status },
    ],
  })

  async function save(payload: ManualBlockPayload, icon: File | null, id?: string) {
    try {
      const saved = id
        ? await ManualBlocksService.update(id, payload)
        : await ManualBlocksService.create(payload)
      if (icon) await ManualBlocksService.uploadIcon(saved.id, icon)
      toast.success({ title: id ? 'Bloco atualizado' : 'Bloco criado', description: payload.title ?? '' })
      setOpenForm({ open: false })
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    }
  }

  async function del() {
    if (!openDelete) return
    try {
      await ManualBlocksService.destroy(openDelete.id)
      toast.success({ title: 'Bloco removido', description: openDelete.title })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<ManualBlock>[] = [
    { key: 'order', header: 'Ordem', align: 'center', className: 'w-20', render: b => b.sortOrder ?? 0 },
    { key: 'icon', header: '', align: 'center', className: 'w-14', render: b => b.iconUrl
      ? <img src={b.iconUrl} alt="" className="mx-auto h-7 w-7 object-contain" />
      : <ImageIcon size={16} className="mx-auto text-gray-300" /> },
    { key: 'title', header: 'Bloco', alwaysVisible: true, render: b => (
      <div>
        <div className="font-medium text-gray-800">{b.title}</div>
        {b.subtitle && <div className="text-[11px] text-gray-500">{b.subtitle}</div>}
      </div>
    ) },
    { key: 'coverageItem', header: 'Cobertura', render: b => b.coverageItemName
      ? <span className="text-gray-700">{b.coverageItemName}</span>
      : <span className="text-xs text-gray-400">Fixo (sempre entra)</span> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: b => <StatusDot value={b.status} /> },
  ]

  return (
    <>
      <PageHeader
        title="Manual de utilização"
        subtitle="Blocos de conteúdo usados na geração do manual do beneficiário"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Manual' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo bloco</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.commercial.manualBlocks"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={b => b.id}
        emptyMessage="Nenhum bloco cadastrado."
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(b) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: b }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(b) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <BlockForm
        open={openForm.open}
        item={openForm.item}
        coverageItems={items}
        canUploadIcon={canIcon}
        onClose={() => setOpenForm({ open: false })}
        onSave={save}
      />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={del}
        title="Remover bloco?"
        description={`Remover "${openDelete?.title}" do manual? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function BlockForm({ open, item, coverageItems, canUploadIcon, onClose, onSave }: {
  open: boolean
  item?: ManualBlock
  coverageItems: ProposalCoverageItem[]
  /** POST /settings/manual-blocks/{id}/icon exige `settings.manual_blocks.icon`. */
  canUploadIcon: boolean
  onClose: () => void
  onSave: (payload: ManualBlockPayload, icon: File | null, id?: string) => Promise<void>
}) {
  const [coverageItemId, setCoverageItemId] = useState('')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [description, setDescription] = useState('')
  const [items, setItems] = useState('')
  const [phoneOption, setPhoneOption] = useState('')
  const [flowSteps, setFlowSteps] = useState('')
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [icon, setIcon] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setCoverageItemId(item?.coverageItemId ?? '')
    setTitle(item?.title ?? '')
    setSubtitle(item?.subtitle ?? '')
    setDescription(item?.description ?? '')
    setItems(toLines(item?.items))
    setPhoneOption(item?.phoneOption ?? '')
    setFlowSteps(toLines(item?.flowSteps))
    setSortOrder(item?.sortOrder ?? 0)
    setStatus(item?.status ?? 'ATIVO')
    setIcon(null)
  }, [open, item])

  async function submit() {
    setSaving(true)
    try {
      await onSave({
        proposal_coverage_item_id: coverageItemId || null,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        description: description.trim() || null,
        items: fromLines(items),
        phone_option: phoneOption.trim() || null,
        flow_steps: fromLines(flowSteps),
        sort_order: sortOrder ?? 0,
        status,
      }, icon, item?.id)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={item ? 'Editar bloco do manual' : 'Novo bloco do manual'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={saving} disabled={!title.trim()}>Salvar</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-md bg-brand-50/60 px-3 py-2 text-xs text-gray-600 ring-1 ring-brand-100">
          <BookOpen size={13} className="mr-1 inline text-brand-600" />
          O bloco entra no manual quando a cobertura vinculada está <strong>inclusa</strong> na proposta.
          Sem cobertura, ele é fixo e entra sempre.
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
          <Select
            label="Cobertura vinculada"
            value={coverageItemId}
            onChange={e => setCoverageItemId(e.target.value)}
            options={coverageItems.map(i => ({ value: i.id, label: i.name }))}
            placeholder="Bloco fixo (sem cobertura)"
            searchPlaceholder="Buscar cobertura"
          />
          <NumberInput
            label="Ordem"
            value={sortOrder}
            onChange={setSortOrder}
            min={0}
            placeholder="Ex.: 1"
          />
        </div>

        <Input
          label="Título"
          required
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="ASSISTÊNCIA MÉDICA"
        />
        <Input
          label="Subtítulo"
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
          placeholder="Até 80% de desconto em consultas"
        />
        <RichTextEditor
          compact
          label="Descrição"
          value={description}
          onChange={setDescription}
          hint="Texto explicativo do benefício, como aparece no manual."
        />
        <div>
          <Textarea
            label="Itens"
            rows={4}
            value={items}
            onChange={e => setItems(e.target.value)}
            placeholder={'Um item por linha. Ex.:\nClínico geral\nCardiologista\nPediatra'}
          />
          <p className="mt-1 text-xs text-gray-500">Aparecem como marcadores ✓ no manual.</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]">
          <Input
            label="Opção no 0800"
            value={phoneOption}
            onChange={e => setPhoneOption(e.target.value)}
            placeholder="Opção 2"
          />
          <Textarea
            label="Etapas do atendimento"
            rows={3}
            value={flowSteps}
            onChange={e => setFlowSteps(e.target.value)}
            placeholder={'Uma etapa por linha. Ex.:\nConfirmação de cadastro\nAgendamento de consulta'}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Ícone</label>
          <div className="flex items-center gap-3">
            {item?.iconUrl && !icon && <img src={item.iconUrl} alt="" className="h-10 w-10 object-contain" />}
            {canUploadIcon ? (
              <input
                type="file"
                accept=".png,.svg,.jpg,.jpeg"
                onChange={e => setIcon(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-brand-500 file:px-3 file:py-2 file:text-white hover:file:bg-brand-600"
              />
            ) : (
              !item?.iconUrl && <span className="text-xs text-gray-400">Sem ícone</span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {canUploadIcon ? 'PNG ou SVG com fundo transparente, até 2 MB.' : 'Sem permissão para enviar ícone.'}
          </p>
        </div>

        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
